import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { DateRange, DurationSelectorProps } from "./types";

import { InlineRangeSelector } from "./InlineRangeSelector";
import { formatButtonLabel } from "./utils/dateUtils";
export const DurationSelector: React.FC<DurationSelectorProps> = ({
  value,
  onChange,
  buttonLabel,
  buttonVariant = "text",
  disabled = false,
  error = false,
  helperText,
  ...otherProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [tempValue, setTempValue] = React.useState<DateRange>(value);
  const open = Boolean(anchorEl);

  React.useEffect(() => {
    setTempValue(value);
  }, [value[0]?.valueOf(), value[1]?.valueOf()]);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setTempValue(value); // Reset temp value when opening
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTempValue(value);
  };

  const handleApply = () => {
    onChange(tempValue);
    setAnchorEl(null);
  };

  const handleReset = () => {
    const emptyRange: DateRange = [null, null];
    setTempValue(emptyRange);
    onChange(emptyRange);
    setAnchorEl(null);
  };

  return (
    <Box>
      {/* Trigger Button */}
      <Button
        variant={buttonVariant}
        onClick={handleButtonClick}
        disabled={disabled}
        startIcon={<CalendarTodayOutlinedIcon fontSize="small" />}
        sx={{
          borderColor: error ? "error.main" : undefined,
          color: error ? "error.main" : undefined,
        }}
      >
        {formatButtonLabel(buttonLabel || "", tempValue)}
      </Button>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={
          isMobile
            ? { vertical: "bottom", horizontal: "center" }
            : { vertical: "bottom", horizontal: "left" }
        }
        transformOrigin={
          isMobile
            ? { vertical: "top", horizontal: "center" }
            : { vertical: "top", horizontal: "left" }
        }
        slotProps={{
          paper: {
            sx: {
              ...(isMobile && {
                width: "100vw",
                maxWidth: "100vw",
                left: "0 !important",
                right: "0 !important",
                borderRadius: 0,
                maxHeight: "90vh",
                overflow: "auto",
              }),
            },
          },
        }}
        {...(isMobile && {
          disableScrollLock: true,
          marginThreshold: 0,
        })}
      >
        <InlineRangeSelector
          value={tempValue}
          onChange={setTempValue}
          onApply={handleApply}
          onReset={handleReset}
          {...otherProps}
        />
      </Popover>
    </Box>
  );
};
